import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { X, Music, Play, Pause } from 'lucide-react';

// Extract YouTube video ID from various URL formats
const extractVideoId = (url) => {
  if (!url) return null;
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/ // Direct video ID
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

export const YouTubePlayer = ({ videoUrl, onClose, onChangeUrl, isMuted }) => {
  const iframeRef = useRef(null);
  const videoId = extractVideoId(videoUrl);

  // Control YouTube player based on mute state
  useEffect(() => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      const command = isMuted ? 'pauseVideo' : 'playVideo';
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: command }),
        '*'
      );
    }
  }, [isMuted]);

  if (!videoId) return null;

  return (
    <div 
      className="fixed z-50 bottom-4 left-4"
      data-testid="youtube-player"
    >
      {/* Hidden iframe for audio playback */}
      <iframe
        ref={iframeRef}
        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&loop=1&playlist=${videoId}&enablejsapi=1`}
        title="YouTube music player"
        className="absolute w-0 h-0 opacity-0 pointer-events-none"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      />
      
      {/* Audio-style player UI */}
      <div className={cn(
        "flex items-center gap-3 px-4 py-3 rounded-full",
        "bg-[#1e2a4a]/95 backdrop-blur-sm",
        "border-2 border-[#e89b47]/40",
        "shadow-lg shadow-[#e89b47]/20",
        isMuted && "opacity-60"
      )}>
        {/* Music icon with pulse animation */}
        <div className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center",
          "bg-[#e89b47]/20 border border-[#e89b47]/40"
        )}>
          <Music className={cn(
            "w-5 h-5 text-[#e89b47]",
            !isMuted && "animate-pulse"
          )} />
        </div>
        
        {/* Now playing text */}
        <div className="flex flex-col">
          <span className="text-[#d4c4a8]/50 text-xs">
            {isMuted ? 'Paused' : 'Now Playing'}
          </span>
          <span className="text-[#e8dcc8] text-sm font-body max-w-[120px] truncate">
            YouTube Audio
          </span>
        </div>
        
        {/* Animated bars - only show when not muted */}
        {!isMuted && (
          <div className="flex items-end gap-[3px] h-4 mx-2">
            <div className="w-1 bg-[#e89b47] rounded-full animate-pulse" style={{ height: '60%', animationDelay: '0ms' }} />
            <div className="w-1 bg-[#e89b47] rounded-full animate-pulse" style={{ height: '100%', animationDelay: '150ms' }} />
            <div className="w-1 bg-[#e89b47] rounded-full animate-pulse" style={{ height: '40%', animationDelay: '300ms' }} />
            <div className="w-1 bg-[#e89b47] rounded-full animate-pulse" style={{ height: '80%', animationDelay: '450ms' }} />
          </div>
        )}
        
        {/* Paused indicator */}
        {isMuted && (
          <div className="flex items-center gap-1 mx-2 text-[#d4c4a8]/50">
            <Pause className="w-4 h-4" />
          </div>
        )}
        
        {/* Change button */}
        <button
          onClick={onChangeUrl}
          className="text-[#d4c4a8]/50 hover:text-[#e89b47] transition-colors text-xs px-2"
          title="Change track"
        >
          Change
        </button>
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="text-[#d4c4a8]/50 hover:text-red-400 transition-colors p-1"
          title="Close"
          data-testid="youtube-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default YouTubePlayer;
